import _includes from "lodash/includes";
import { normalize, schema } from "normalizr";
import _fetch from "fetchival";
import { camelizeKeys } from "humps";
import Http from "fetch-run";
import createSetHeaders from "fetch-run/src/use/headers";
import jsonResponse from "fetch-run/src/use/json";
import createErrorHandler from "fetch-run/src/use/error";
import createNormalize from "fetch-run/src/use/normalize";
//
import Storage from "./Storage";
import socket from "./socket";
import setTokens, { setAccessToken, setSocketId } from "./http/tokens";
import createCamelizeResponse from "./http/camelize";
import createRefreshToken, { setRefreshToken } from "./http/refresh-token";
import { getUnixTimestamp } from "../utils";

//  API
class Api extends Http {
  countModels(cityId, date, duration, outcall, genderId, filters, areaId) {
    return this.get(
      "models-count",
      getSearchParams(
        cityId,
        date,
        duration,
        outcall,
        genderId,
        filters,
        areaId
      )
    );
  }

  getModelsOnline(cityId, date, duration, outcall, genderId, filters, areaId) {
    return this.get(
      "models",
      getSearchParams(
        cityId,
        date,
        duration,
        outcall,
        genderId,
        filters,
        areaId
      )
    );
  }

  getModelsOffline(cityId, date, duration, outcall, genderId, filters, areaId) {
    return this.get(
      "models/offline",
      getSearchParams(
        cityId,
        date,
        duration,
        outcall,
        genderId,
        filters,
        areaId
      )
    );
  }

  getModelsFavorite() {
    return this.get("me/models");
  }

  getVideos(cityId, genderId) {
    return this.get("videos?city_id=" + cityId + "&gender_id=" + genderId);
  }

  getModel(modelId, isOnline) {
    return this.get(`models/${modelId}`, { online: isOnline ? 1 : 0 });
  }

  getModelPremium(modelId, isOnline) {
    const online = isOnline ? 1 : 0;

    return this.get(`models/${modelId}/premium`, { online });
  }

  getPhoneCountries(query) {
    return this.get("countries", { query });
  }

  getStates(countryId) {
    return this.get(`countries/${countryId}/states`);
  }

  sendSmsOtp(booking, phone, countryId) {
    return this.post(`bookings/${booking.id}/sms-otp`, {
      phone_national: phone,
      country_id: countryId,
    });
  }

  sendCallOtp(bookingId) {
    return this.post(`bookings/${bookingId}/call-otp`);
  }

  sendEmailOtp(bookingId) {
    return this.post(`bookings/${bookingId}/email-otp`);
  }

  verifyBooking(bookingId, otp) {
    return this.post(`bookings/${bookingId}/verify`, { otp });
  }

  verifyBookingManually(bookingId) {
    return this.post(`bookings/${bookingId}/verify-manually`);
  }

  preBookModel(modelId, cityId, date, duration) {
    return this.post(`models/${modelId}/pre-book`, {
      city_id: cityId,
      date: getUnixTimestamp(date),
      duration,
    });
  }

  createBooking(
    booking,
    phoneCountryId,
    cityId,
    modelId,
    locationId,
    utmSource,
    tapClick,
    ga
  ) {
    const data = {
      model_id: modelId,
      outcall: booking.outcall,
      city_id: cityId,
      date: getUnixTimestamp(booking.date),
      duration: booking.duration,
      services: booking.services,
      email: booking.email,
      phone: booking.phone,
      phone_country: phoneCountryId,
      fullname: booking.fullname,
      message: booking.message,
      utm_source: utmSource,
      tap_click: tapClick,
      ga,
      contact_provider: booking.contact_provider,
      contact_provider_id: booking.contact_provider_id,
    };

    if (booking.outcall) {
      data.location_id = locationId;
      data.room_number = booking.roomNumber;
    }

    // data._token = window.SERVER.csrf_token;

    return this.post("bookings", data);
  }

  getLocationFee(locationId, modelId) {
    return this.post("travel-fee", {
      location_id: locationId,
      model_id: modelId,
    });
  }

  searchPlace(query, cityId, uuid) {
    return this.get("places", { q: query, city_id: cityId, uuid });
  }

  getLocation(placeId) {
    return this.get(`locations/${placeId}`);
  }

  storeLocation(placeId, uuid) {
    return this.post("locations", { place_id: placeId, uuid });
  }

  getReviews({ modelId, page }) {
    return this.get(`models/${modelId}/reviews`, { page });
  }

  async login(email, password) {
    const csrfToken = window.SERVER.csrf_token;

    try {
      const data = await _fetch("/login", { credentials: "same-origin" }).post({
        email,
        password,
        _token: csrfToken,
      });

      // Api
      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);

      // Socket
      socket.setAccessToken(data.access_token);
      socket.getSocketId().then(setSocketId);

      // Remember tokens
      await Promise.all([
        Storage.set("tokens", data),
        Storage.set("csrf_token", csrfToken),
      ]);
    } catch (err) {
      if (err.response) {
        try {
          err.data = await err.response.json();
        } catch (err) {
          // Ignore JSON error
        }
      }

      throw err;
    }
  }

  getUser() {
    return this.get("me");
  }

  getPlans(regionId) {
    return this.get(`plans?region=${regionId}`);
  }

  reportImage(imageId) {
    return this.post(`me/flags/images/${imageId}`);
  }

  reportReview(reviewId) {
    return this.post(`me/flags/reviews/${reviewId}`);
  }

  favoriteModel(modelId) {
    return this.post(`me/models/${modelId}`);
  }

  unfavoriteModel(modelId) {
    return this.delete(`me/models/${modelId}`);
  }

  createModelAlert(modelId, duration) {
    return this.post(`models/${modelId}/alerts`, { duration });
  }
}

//  Utils
export function normalizeResponse(json, schema) {
  return normalize(camelizeKeys(json), schema);
}

function getSearchParams(
  cityId,
  date,
  duration,
  outcall,
  genderId,
  filters,
  areaId
) {
  const params = {
    city_id: cityId,
    date: getUnixTimestamp(date),
    duration,
    outcall: outcall ? "1" : "0",
    gender_id: genderId,
  };

  if (filters.ageMin) {
    params["age_min"] = filters.ageMin;
  }

  if (filters.ageMax) {
    params["age_max"] = filters.ageMax;
  }

  if (filters.ageVerified) {
    params["age_verified"] = 1;
  }

  if (filters.brandId !== "") {
    if (filters.brandId === "independent") {
      params["independent"] = 1;
    } else {
      params["brand_id"] = filters.brandId;
    }
  }

  if (filters.cup) {
    // Note: Can be "D+", need to encode "+"
    params["cup"] = encodeURIComponent(filters.cup);
  }

  if (filters.heightMin) {
    params["height_min"] = filters.heightMin;
  }

  if (filters.heightMax) {
    params["height_max"] = filters.heightMax;
  }

  if (filters.bodyType) {
    params["body_type"] = filters.bodyType;
  }

  if (filters.endowment) {
    params["endowment"] = filters.endowment;
  }

  if (filters.orientation) {
    params["orientation"] = filters.orientation;
  }

  if ((filters.services || []).length) {
    params["services"] = encodeURIComponent(filters.services.join("+"));
  }

  if (areaId) {
    params["area"] = areaId;
  }

  return params;
}

const { env } = window.SERVER;

const config = {
  API_URL: `${env.API_URL}/${env.API_PREFIX}`,
};

const api = new Api(config.API_URL);

//
// Middlewares

// Request headers
const headers = {
  _prefix: env.API_PREFIX,

  "*": {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};

api.use(createSetHeaders(headers));

// JSON response
api.use(jsonResponse);

// Set access token & socket ID
api.use(setTokens);

// Error management
api.use(createErrorHandler());

// Auto-refresh tokens
api.use(createRefreshToken(api));

// Normalize responses
const countrySchema = new schema.Entity("countries");
const serviceSchema = new schema.Entity(
  "services",
  {},
  {
    idAttribute: function (service, model) {
      return `m${model.id}-s${service.id}`;
    },
  }
);
const brandSchema = new schema.Entity("brands");
const rateSchema = new schema.Entity("rates");
const locationSchema = new schema.Entity("locations");
const placeSchema = new schema.Entity("places", {}, { idAttribute: "placeId" });
const reviewSchema = new schema.Entity("reviews");
const modelSchema = new schema.Entity(
  "models",
  {
    brand: brandSchema,
    rates: [rateSchema],
    services: [serviceSchema],
    reviews: [reviewSchema],
  },
  {
    processStrategy: (value) => ({
      ...value,
      city_id: value.city.id,
      city: {
        ...value.city,
        multi: value.city.countryId === 528 ? [7, 10, 11] : [], // 528 = NL
      },
    }),
  }
);

const videoSchema = new schema.Entity("videos", {
  model: modelSchema,
});

export const Schemas = {
  country: countrySchema,
  service: serviceSchema,
  rate: rateSchema,
  model: modelSchema,
  place: placeSchema,
  video: videoSchema,
};

const schemas = {
  _prefix: env.API_PREFIX,

  get: {
    "^models$": [modelSchema],
    "^models/offline$": [modelSchema],
    "^me/models$": [modelSchema],
    "^models/\\d+$": modelSchema,
    "^models/\\d+/premium$": modelSchema,
    "^countries$": [countrySchema],
    "^places$": [placeSchema],
    "^locations/[\\w-]+$": locationSchema,
    "^models/\\d+/reviews$": [reviewSchema],
    "^videos$": [videoSchema],
  },

  post: {
    "^locations$": locationSchema,
  },
};

// Camel case response
api.use(createCamelizeResponse(schemas));
api.use(createNormalize(schemas));

export default api;
